import { useCallback, useEffect, useImperativeHandle, useRef } from "react";

import { CodeNode } from "@lexical/code";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { mergeRegister } from "@lexical/utils";
import { NamePath } from "antd/lib/form/interface";
import { clsx } from "clsx";
import {
    $getRoot,
    $insertNodes,
    $setSelection,
    EditorState,
    LexicalEditor,
    CLEAR_EDITOR_COMMAND,
    LineBreakNode,
    RootNode,
} from "lexical";

import { RVLexicalPlaceholder } from "./Components/RVLexicalPlaceholder/RVLexicalPlaceholder";
import { RVLexicalEditor } from "./RVLexicalEditor";
import { escapeCustomTags } from "./utils/serialization";

import styles from "./SimpleTextEditor.module.scss";

type SimpleTextEditorProps = Omit<
    React.ComponentProps<typeof RVLexicalEditor>,
    "placeholder" | "value" | "onChange" | "editorRef"
> & {
    /**
     * This has to be the same value as the `<Form.Item name="..."/>`
     */
    className?: string;
    inputClassName?: string;
    formItemName?: NamePath;
    isPlainText: boolean;
    isJson?: boolean;
    placeholder?: string;
    value?: string;
    escapeHTML?: boolean;
    /** Preserves all XML tags by escaping on load and unescaping on save. Use for AI prompts. */
    preserveXmlTags?: boolean;
    singleRow?: boolean;
    onChange?: (value: string) => void;
    inputRef?: React.Ref<LexicalEditor>;
};

function SimpleTextEditor({
    id,
    className,
    inputClassName,
    formItemName,
    isPlainText,
    isJson = false,
    placeholder,
    value,
    escapeHTML,
    preserveXmlTags,
    singleRow,
    onChange,
    inputRef,
    ...props
}: SimpleTextEditorProps) {
    const editorRef = useRef<LexicalEditor>();

    // allows consumer to have access to the editor instance (mostly for dispatching commands)
    useImperativeHandle(inputRef, () => editorRef.current as LexicalEditor);

    // [convenience]
    // antd passes the field input's name as the id
    // with all the name paths concatenated with an underscore
    // so instead of forcing people to specify the formItemName all the time
    // we can deduct what the name is, thus making the property optional
    // (formItemName is needed to manually trigger the validation when the user types)
    formItemName ||= (id ?? "").split("_") as NamePath;
    const serializeOnChange = useCallback(
        (state: EditorState) => {
            const value = state.read(() => {
                const textContent = $getRoot().getTextContent();

                if (isPlainText) {
                    // No need to unescape tags here: DOMParser already decodes entities (see serialization.unit.test.ts:143-144)
                    return textContent;
                }

                if (!textContent.length) {
                    // rich text's default empty value is a paragraph with a new line character
                    // so instead of returning that (which would not be an empty string)
                    // we'll forcefully return an empty string
                    return "";
                }

                return $generateHtmlFromNodes(editorRef?.current as LexicalEditor);
            });
            onChange?.(value);
        },
        [isPlainText, onChange]
    );

    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.update(() => {
                if (value) {
                    // for plain text editors, we want to preserve new lines
                    // so we'll transform them into `<br>` tags
                    let textContent = isPlainText ? value.replace(/\n/g, "<br>") : value;

                    // TODO: we need to move this logic into a separate plugin and refactor
                    // the editor update function
                    if (escapeHTML || preserveXmlTags) {
                        // Escape XML/HTML tags to prevent DOMParser from stripping them
                        textContent = escapeCustomTags(textContent, ["br"]);
                    }

                    const parser = new DOMParser();
                    // for fields representing json, we need to respect white spaces for json formatting (hence the pre)
                    const dom = parser.parseFromString(isJson ? `<pre>${textContent}</pre>` : textContent, "text/html");
                    const nodes = $generateNodesFromDOM(editorRef.current as LexicalEditor, dom);
                    $getRoot().clear();
                    $insertNodes(nodes);
                    $setSelection(null);
                }
            });
        }

        // we only want this to run once, not every time the value changes
        // so we can set the initial value
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // prevent from auto focusing the editor when the component mounts
    useEffect(() => {
        if (editorRef.current) {
            setTimeout(() => editorRef.current?.blur());
        }
    }, []);

    // we need to clear the editor when the value is an empty string
    useEffect(() => {
        if (value == "" && editorRef.current) {
            editorRef.current.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
        }
        // Remove the last line break if the editor is in single row mode
        if (singleRow && editorRef.current) {
            mergeRegister(
                editorRef.current.registerNodeTransform(RootNode, (rootNode: RootNode) => {
                    if (rootNode.getChildrenSize() <= 1) {
                        return;
                    }
                    rootNode.getLastChild()?.remove();
                }),
                editorRef.current.registerNodeTransform(LineBreakNode, (node) => {
                    node.remove();
                })
            );
        }
    }, [singleRow, value]);

    return (
        <div className={clsx(styles.wrapper, className)}>
            <RVLexicalEditor
                editorRef={editorRef}
                isPlainText={isPlainText}
                className={clsx(styles.input, inputClassName)}
                placeholder={
                    placeholder ? <RVLexicalPlaceholder text={placeholder} className={styles.placeholder} /> : null
                }
                onChange={serializeOnChange}
                {...props}
            />
        </div>
    );
}

type PlainTextEditorProps = Omit<React.ComponentProps<typeof SimpleTextEditor>, "isPlainText">;

/**
 * `PlainTextEditor` is not a controlled input
 * this means that the value will only be read once, after the first render.
 * If you ever need to update the value of this editor, you need to re-render it.
 * The easiest solution is to add a `key` property on it and let React handle it.
 * @param PlainTextEditorProps props
 * @returns PlainTextEditor
 */
export function PlainTextEditor(props: PlainTextEditorProps) {
    return <SimpleTextEditor {...props} isPlainText={true} />;
}

type RichTextEditorProps = Omit<React.ComponentProps<typeof SimpleTextEditor>, "isPlainText" | "escapeHTML">;

/**
 * `RichTextEditor` is not a controlled input
 * this means that the value will only be read once, after the first render.
 * If you ever need to update the value of this editor, you need to re-render it.
 * The easiest solution is to add a `key` property on it and let React handle it.
 * @param RichTextEditorProps props
 * @returns RichTextEditor
 */
export function RichTextEditor({ nodeOverrides, ...props }: RichTextEditorProps) {
    return (
        <SimpleTextEditor
            {...props}
            isPlainText={false}
            nodeOverrides={[
                ListNode,
                ListItemNode,
                HorizontalRuleNode,
                HeadingNode,
                QuoteNode,
                CodeNode,
                LinkNode,
                ...(nodeOverrides ?? []),
            ]}
        />
    );
}
